import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { CarriersInfo, LastLoads } from 'fragments';
import {
  agentActions,
  branchActions,
  carrierActions,
  factoringActions,
  httpRequestsOnSuccessActions,
  officeActions,
} from 'store';
import {
  ActiveInactive,
  CreateWrapperHead,
  History,
  Loader, NoteDrawer,
  SimpleModal,
  SimpleTabs,
  SlicedText, TextToolTip, useWidth,
} from 'components';
import {
  AntSwitch,
  FindLoad,
  FindSuccessItem,
  Images, isAdmin, PermissionsList,
  RenderStatus, RolePermission,
  useGlobalStyles,
  userType,
} from 'utils';
import { carrierInfoFragments, CarrierLoadsTable, CollectionsTable } from '../../fragments/carriers/carrierInfo/core';
import { Payments } from '../../fragments/carriers/carrierInfo/core/payments';
import { Equipments } from './fragments/equipments';
import { Other } from './fragments/other/other';
import { vendorEnums } from '../vendors';
import { Reserve } from '../../fragments/carriers/carrierInfo/core/reserve';
import { carrierIntegrationEnums } from '../../fragments/carriers/carrierInfo/core/constants';
import { GoHighwayInfo } from './fragments/goHighwayInfo';
import { renderGoHighwayInfoStatus } from '../../fragments/carriers/createCarrier/core/constants';

export const CarrierInfoPage = () => {
  const { carrierById } = useSelector((state) => ({
      carrierById: state.carriers.carrierById,
    }),
  );
  const dispatch = useDispatch();
  const params = useParams();
  const globalStyle = useGlobalStyles();
  const classes = carrierInfoFragments();
  const [switchBoolean, setSwitchBoolean] = useState(false);
  const [carrierName, setCarrierName] = useState('');
  const [open, setOpen] = useState(false);
  const loader = FindLoad('GET_CARRIER_BY_ID');
  const name = carrierName ? carrierName : carrierById && carrierById.companyName;
  const activeInactive = switchBoolean === true ? 'Inactivate' : 'Activate';
  const activeInactiveSuccess = FindSuccessItem('INACTIVATE_ACTIVATE_CARRIER');
  const activeInactiveLoader = FindLoad('INACTIVATE_ACTIVATE_CARRIER');
  const history = useHistory();
  const info = history?.location?.state;
  const checkEquipment =
    (info?.vendorType || carrierById?.type) !== vendorEnums.FACTORED &&
    (info?.vendorType || carrierById?.type) !== vendorEnums.VENDOR &&
    (info?.vendorType || carrierById?.type) !== vendorEnums.INHOUSE_PROFILE;
  const width = useWidth();

  useEffect(() => {
    if (carrierById) {
      setSwitchBoolean(carrierById?.status === 'ACTIVE');
    }
  }, [carrierById]);

  useEffect(() => {
    dispatch(carrierActions.getCarrierById(params.id));
    dispatch(agentActions.getAgents({ status: 'ACTIVE' }));
    dispatch(officeActions.getOffices({ status: 'ACTIVE' }));
    dispatch(branchActions.getBranches({ status: 'ACTIVE' }));
    dispatch(carrierActions.getCarrierOfficeAccess(params.id, 'ASSIGNED_OFFICES'));
    dispatch(carrierActions.getCarrierAgentAccess(params.id, 'ASSIGNED_AGENTS'));
    dispatch(carrierActions.getCarrierBranchAccess(params.id, 'BLACKLISTED_BRANHCES'));
    dispatch(carrierActions.getPaymentsCarrierById(params.id));
    dispatch(factoringActions.getFactorings({ status: 'ACTIVE' }));
  }, [params.id]);

  useEffect(() => {
    if (activeInactiveSuccess) {
      dispatch(httpRequestsOnSuccessActions.removeSuccess('INACTIVATE_ACTIVATE_CARRIER'));
      const status = carrierById.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      setSwitchBoolean(status);
      setOpen(false);
    }
  }, [activeInactiveSuccess]);

  const handleOpenClose = () => {
    setOpen(!open);
  };

  const handleSwitch = () => {
    const status = carrierById.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    dispatch(carrierActions.inactivateActivateCarrier(carrierById.id, status));
  };

  const tabsLabels = [
    {
      label: 'Details',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.CREATE_UPDATE_CARRIER?.code, PermissionsList.READ_CARRIER?.code]) && checkEquipment &&
    {
      label: 'Equipment',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.READ_CARRIER_LOADS?.code]) &&
    {
      label: 'Loads',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.READ_CARRIER_LOADS?.code], 'agentView') &&
    {
      label: 'Last Loads',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.READ_CARRIER_COLLECTION?.code]) &&
    {
      label: 'Collections',
    },
    userType === 'ADMIN' &&
    {
      label: 'Payments',
    },
    {
      label: 'History',
    },
    isAdmin &&
    {
      label: 'Reserve',
    },
    (carrierById?.onboardingSource === carrierIntegrationEnums.GO_HIGHWAY && carrierById?.type === vendorEnums.CARRIER) && {
      label: 'Go Highway',
    },
    {
      label: 'Other',
    },
  ];

  const tabsContent = [
    {
      tabComponent: <CarriersInfo info={carrierById} setCarrierName={setCarrierName} />,
      tab: 'Details',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.CREATE_UPDATE_CARRIER?.code, PermissionsList.READ_CARRIER?.code]) && checkEquipment &&
    {
      tabComponent: <Equipments />,
      tab: 'Equipment',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.READ_CARRIER_LOADS?.code]) &&
    {
      tabComponent: <CarrierLoadsTable />,
      tab: 'Loads',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.READ_CARRIER_LOADS?.code], 'agentView') &&
    {
      tabComponent: <LastLoads />,
      tab: 'Last Loads',
    },
    RolePermission([PermissionsList.ALL_ACCESS?.code, PermissionsList.READ_CARRIER_COLLECTION?.code]) &&
    {
      tabComponent: <CollectionsTable info={carrierById} />,
      tab: 'Collections',
    },
    userType === 'ADMIN' &&
    {
      tabComponent: <Payments info={carrierById} />,
      tab: 'Payments',
    },
    {
      tabComponent: <History onModel={'carrier'} resourceId={params.id} />,
      tab: 'History',
    },
    isAdmin &&
    {
      tabComponent: <Reserve />,
      tab: 'Reserve',
    },

    (carrierById?.onboardingSource === carrierIntegrationEnums.GO_HIGHWAY && carrierById?.type === vendorEnums.CARRIER) && {
      tabComponent: <GoHighwayInfo carrierId={params.id} />,
      tab: 'Go Highway',
    },
    {
      tabComponent: <Other />,
      tab: 'Other',
    },
  ];

  if (loader.length) return <Loader />;


  return (
    <>
      {(carrierById?.onboardingSource === carrierIntegrationEnums.GO_HIGHWAY && carrierById?.type === vendorEnums.CARRIER) &&
        <div className='carrierInfo-tabs-header-goHighway'>
          <div className={globalStyle.centerItem} style={{ gap: 12 }}>
            <img src={Images.carrierBold} alt={'authorityBlueFill'} />

            <TextToolTip
              text={name}
              content={name}
              classNameText={'carrierInfo-tabs-header-goHighway-title'}
            />
          </div>

          <div className='carrierInfo-tabs-header-goHighway-items-wrapper'>
            <div className='carrierInfo-tabs-header-goHighway-item-block'>
              <p className='carrierInfo-tabs-header-goHighway-item-text' style={{ fontWeight: '700' }}>
                MC:
              </p>

              <TextToolTip
                classNameText='carrierInfo-tabs-header-goHighway-item-text'
                text={carrierById?.mcNumber || 'N/A'}
                content={carrierById?.mcNumber || 'N/A'}
              />
            </div>

            <div className='carrierInfo-tabs-header-goHighway-item-line' />

            <div className='flex-align-center' style={{ gap: 16 }}>
              <div className='carrierInfo-tabs-header-goHighway-item-block'>
                <p className='carrierInfo-tabs-header-goHighway-item-text' style={{ fontWeight: '700' }}>
                  DOT:
                </p>

                <TextToolTip
                  classNameText='carrierInfo-tabs-header-goHighway-item-text'
                  text={carrierById?.dot || 'N/A'}
                  content={carrierById?.dot || 'N/A'}
                />
              </div>

              <div
                className='status-box-goHighWay-header'
                style={{
                  background: renderGoHighwayInfoStatus(carrierById?.isDotActive ? 'Active' : 'Inactive')?.backgroundColor,
                  color: renderGoHighwayInfoStatus(carrierById?.isDotActive ? 'Active' : 'Inactive')?.color
                }}
              >
                {carrierById?.isDotActive ? 'Active' : 'Inactive'}
              </div>
            </div>

            <div className='carrierInfo-tabs-header-goHighway-item-line' />

            <div className='carrierInfo-tabs-header-goHighway-item-block'>
              <p className='carrierInfo-tabs-header-goHighway-item-text' style={{ fontWeight: '700' }}>
                CA:
              </p>

              <TextToolTip
                classNameText='carrierInfo-tabs-header-goHighway-item-text'
                text={carrierById?.intrastateNumber || 'N/A'}
                content={carrierById?.intrastateNumber || 'N/A'}
              />
            </div>

            <div className='carrierInfo-tabs-header-goHighway-item-block'>
              <p className='carrierInfo-tabs-header-goHighway-item-text' style={{ fontWeight: '700' }}>
                SCAC:
              </p>

              <TextToolTip
                classNameText='carrierInfo-tabs-header-goHighway-item-text'
                text={carrierById?.scac || 'N/A'}
                content={carrierById?.scac || 'N/A'}
              />
            </div>
          </div>
        </div>
      }
      <SimpleTabs
        parentLink={'/Carriers'}
        parent={'Carriers'}
        child={'Carrier Info'}
        headerWidth={width < 1000 ? '150px' : '500px'}
        position={'relative'}
        head={
          <CreateWrapperHead>
            <div className='space-between' style={{ gap: 15 }}>
              <div className={classes.rateSwitch}>
                {RenderStatus(activeInactive)}
                {RolePermission([PermissionsList.CARRIER_STATUS?.code]) &&
                  <AntSwitch
                    onClick={handleOpenClose}
                    checked={switchBoolean}
                  />
                }
              </div>
              <NoteDrawer
                title={'Carrier'}
              />
              {!(carrierById?.onboardingSource === carrierIntegrationEnums.GO_HIGHWAY && carrierById?.type === vendorEnums.CARRIER) &&
                <div className={globalStyle.centerItem}>
                  <img src={Images.carrierBold} alt={'authorityBlueFill'} />
                  <SlicedText type={'tableName'} size={20} data={name && name} />
                </div>
              }

            </div>
          </CreateWrapperHead>
        }
        border={'blue'}
        height={'full'}
        tabsLabels={tabsLabels}
        tabsContent={tabsContent}
        defaultTab={'Details'}
        paramsName={'carrierParams'}
        headStyles={{ right: 0 }}
      />
      <SimpleModal
        handleOpenClose={handleOpenClose}
        openDefault={open}
        content={
          <ActiveInactive
            loading={!!activeInactiveLoader?.length}
            text={switchBoolean === 'ACTIVE' ? `Inactivate ${name}` : `Activate ${name}`}
            textInfo={switchBoolean === 'ACTIVE' ? 'Inactivate' : 'Activate'}
            name={name}
            handleClose={handleOpenClose}
            handleDel={handleSwitch}
          />
        }
      />
    </>
  );
};